import { useApolloClient } from '@apollo/client'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Dropdown from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import { ForbiddenIcon } from 'components/Icons/ForbiddenIcon'
import { VehicleIcon } from 'components/Icons/VehicleIcon'
import IconButton from 'components/v2/IconButton'
import { ChangeCrewUsersModal } from 'features/Trips/components/ChangeCrewUsersModal'
import { ChangeDriverModal } from 'features/Trips/components/ChangeDriverModal'
import CreateIncidenceModal from 'features/Trips/components/CreateIncidenceModal'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import List from 'components/v2/List'
import Snackbar from 'components/Snackbar'
import { ToggleTripModal } from 'features/Trips/components/ToggleTripModal'
import { isCreateIncidenceCouponsEnabled as isCreateIncidenceCouponsEnabledFn } from 'models/trips'
import { resolvers as tripsResolvers, canBeDeactivated, canBeCancelled } from 'resolvers/trips'
import {
  canActivateTripSale,
  canCancelTrip,
  canChangeTripVehicle,
  canChangeCrewUsers,
  canDeactivateTripSale,
  canReadPassengers,
  canCreateSubcampaign,
  canChangeTripDriver,
  canChangeTripConfirmationModalities,
  canChangePastTripVehicle,
  canChangeItineraryTrip
} from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { SwitchIcon } from 'components/Icons/SwitchIcon'
import { UserPinIcon } from 'components/Icons/UserPinIcon'
import { PassengersIcon } from 'components/Icons/PassengersIcon'
import { CalendarNotesIcon } from 'components/Icons/CalendarNotesIcon'
import { UnexpectedCouponIcon } from 'components/Icons/UnexpectedCouponIcon'
import { PersonAddIcon } from 'components/Icons/PersonAddIcon'
import { CancelTripsModal } from 'features/Trips/components/CancelTripsModal'
import { ChangeTripsVehicleModal } from 'features/Trips/components/ChangeTripsVehicleModal'
import { GTTripV2, GTTripV2SaleStatus, GTTripV2Status } from 'gql/types.generated'
import { ModalityIcon } from 'components/Icons/ModalityIcon'
import { canAddReservedModality, canRemoveReservedModality } from 'features/Trips/utils'
import { AddReservedConfirmationModalityModal } from 'features/Trips/components/AddReservedConfirmationModalityModal'
import { RemoveReservedConfirmationModalityModal } from 'features/Trips/components/RemoveReservedConfirmationModal'

const DeactivateWrapper = styled.div`
  display: flex;
  align-items: center;
`

interface ITripsActionsProps {
  trip: GTTripV2
  selectedTrips: GTTripV2[]
}

export const TripsActions = ({ trip, selectedTrips }: ITripsActionsProps): ReactElement => {
  const client = useApolloClient()
  const location = useLocation()
  client.addResolvers(tripsResolvers)
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [isOpenCancelTripModal, setOpenCancelTripModal] = useState(false)
  const [isOpenToggleTripModal, setOpenToggleTripModal] = useState(false)
  const [isOpenChangeVehicleModal, setOpenChangeVehicleModal] = useState(false)
  const [isOpenChangeDriverModal, setOpenChangeDriverModal] = useState(false)
  const [isOpenChangeCrewUsersModal, setOpenChangeCrewUsersModal] = useState(false)
  const [isOpenIncidenceModal, setOpenIncidenceModal] = useState(false)
  const [isOpenRemoveReservedConfirmationModalityModal, setOpenRemoveReservedConfirmationModalityModal] =
    useState(false)
  const [isOpenAddReservedConfirmationModalityModal, setOpenAddReservedConfirmationModalityModal] = useState(false)
  const [successSnackbar, setSuccessSnackbar] = useState(null)

  const navigate = useNavigate()
  const login = useLogin()

  const toggleDropdown = (): TSFixMe => {
    setOpenDropdown(!isOpenDropdown)
  }

  const { policies } = login.accessToken
  const isCompleted = trip.status === GTTripV2Status.Completed
  const canCancel = canCancelTrip(policies) && canBeCancelled(trip)
  const canChangeFutureTripVehicle = !isCompleted && canChangeTripVehicle(policies)
  const canChangeVehicleForCompletedTrip = isCompleted && canChangePastTripVehicle(policies)
  const canChangeVehicle = canChangeFutureTripVehicle || canChangeVehicleForCompletedTrip
  const canChangeDriver = canChangeTripDriver(policies)
  const canToggleTripActivation =
    canBeDeactivated(trip) &&
    ((trip.saleStatus === GTTripV2SaleStatus.Deactivated && canActivateTripSale(policies)) ||
      (trip.saleStatus === GTTripV2SaleStatus.Activated && canDeactivateTripSale(policies)))
  const canEditItinerary = canChangeItineraryTrip(policies)
  const showPassengersLink = canReadPassengers(policies)
  const canChangeTripCrewUsers = canChangeCrewUsers(FEATURES.TRIP, policies)
  const isCreateIncidenceCouponsEnabled = isCreateIncidenceCouponsEnabledFn(trip)
  const isShowingCreateIncidenceCoupons = canCreateSubcampaign(policies)
  const buttonIsDisabled = selectedTrips.length >= 2
  const canChangeModalityEnabled = canChangeTripConfirmationModalities(policies)
  const isRemoveReservedModalityEnabled = canChangeModalityEnabled && canRemoveReservedModality(trip)
  const isAddReservedModalityEnabled = canChangeModalityEnabled && canAddReservedModality(trip)
  const isChangeModalityEnabled = isRemoveReservedModalityEnabled || isAddReservedModalityEnabled

  const handleCancelTrip = (): TSFixMe => {
    if (canCancel) {
      setOpenDropdown(false)
      setOpenCancelTripModal(true)
    }
  }

  const handleToggleSellActivation = (): TSFixMe => {
    if (canToggleTripActivation) {
      setOpenDropdown(false)
      setOpenToggleTripModal(true)
    }
  }

  const handleChangeVehicle = (): void => {
    if (canChangeVehicle) {
      setOpenDropdown(false)
      setOpenChangeVehicleModal(true)
    }
  }

  const handleChangeDriver = (): void => {
    if (canChangeDriver) {
      setOpenDropdown(false)
      setOpenChangeDriverModal(true)
    }
  }

  const handleChangeCrewUsers = (): void => {
    if (canChangeTripCrewUsers) {
      setOpenDropdown(false)
      setOpenChangeCrewUsersModal(true)
    }
  }

  const handleCreateIncidence = (): void => {
    if (isCreateIncidenceCouponsEnabled) {
      setOpenDropdown(false)
      setOpenIncidenceModal(true)
    }
  }

  const handleChangeConfirmationModalities = (): void => {
    if (isChangeModalityEnabled) {
      setOpenDropdown(false)

      if (isRemoveReservedModalityEnabled) {
        setOpenRemoveReservedConfirmationModalityModal(true)
      } else {
        setOpenAddReservedConfirmationModalityModal(true)
      }
    }
  }

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): TSFixMe => setOpenDropdown(false)}>
        <IconButton onClick={toggleDropdown} disabled={buttonIsDisabled} icon={<DotsIcon />} noBorder smallPadding />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.Item action onClick={handleChangeVehicle} disabled={!canChangeVehicle}>
              <List.Icon>
                <VehicleIcon />
              </List.Icon>
              <FormattedMessage id="trips.actions.changeVehicle.label" defaultMessage="Change vehicle" />
            </List.Item>
            <List.Item action onClick={handleChangeDriver} disabled={!canChangeDriver}>
              <List.Icon>
                <PersonAddIcon />
              </List.Icon>
              <FormattedMessage id="trips.actions.changeDriver.label" defaultMessage="Assign driver" />
            </List.Item>
            <List.Item action onClick={handleChangeCrewUsers} disabled={!canChangeTripCrewUsers}>
              <List.Icon>
                <UserPinIcon />
              </List.Icon>
              <FormattedMessage id="trips.actions.changeCrewUsers.label" defaultMessage="Change crew users" />
            </List.Item>
            <List.Item action onClick={handleToggleSellActivation} disabled={!canToggleTripActivation}>
              <List.Icon>
                <SwitchIcon />
              </List.Icon>
              <DeactivateWrapper>
                {trip.saleStatus === GTTripV2SaleStatus.Deactivated ? (
                  <FormattedMessage id="dictionary.activate" defaultMessage="Activate" />
                ) : (
                  <FormattedMessage id="dictionary.deactivate" defaultMessage="Deactivate" />
                )}
              </DeactivateWrapper>
            </List.Item>
            <List.Item action noPadding={showPassengersLink} disabled={!showPassengersLink}>
              {showPassengersLink ? (
                <List.Link isAction to={`/trips/${trip.id}/passengers`}>
                  <List.Icon>
                    <PassengersIcon />
                  </List.Icon>
                  <FormattedMessage id="dictionary.passengers" defaultMessage="Passengers" />
                </List.Link>
              ) : (
                <>
                  <List.Icon>
                    <PassengersIcon />
                  </List.Icon>
                  <FormattedMessage id="dictionary.passengers" defaultMessage="Passengers" />
                </>
              )}
            </List.Item>
            <List.Item action noPadding={canEditItinerary} disabled={!canEditItinerary}>
              {canEditItinerary ? (
                <List.Link isAction to={`/trips/${trip.id}`} state={{ from: `${location.pathname}${location.search}` }}>
                  <List.Icon>
                    <CalendarNotesIcon />
                  </List.Icon>
                  <FormattedMessage id="trips.actions.editItinerary.label" defaultMessage="Edit itinerary" />
                </List.Link>
              ) : (
                <>
                  <List.Icon>
                    <CalendarNotesIcon />
                  </List.Icon>
                  <FormattedMessage id="trips.actions.editItinerary.label" defaultMessage="Edit itinerary" />
                </>
              )}
            </List.Item>
            {isShowingCreateIncidenceCoupons && (
              <List.Item action onClick={handleCreateIncidence} disabled={!isCreateIncidenceCouponsEnabled}>
                <List.Icon>
                  <UnexpectedCouponIcon />
                </List.Icon>
                <FormattedMessage id="trips.actions.createIncidence.label" defaultMessage="Create incidence coupons" />
              </List.Item>
            )}
            <List.Item action onClick={handleCancelTrip} disabled={!canCancel}>
              <List.Icon>
                <ForbiddenIcon />
              </List.Icon>
              <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
            </List.Item>
            <List.Item action onClick={handleChangeConfirmationModalities} disabled={!isChangeModalityEnabled}>
              <List.Icon>
                <ModalityIcon />
              </List.Icon>
              {isRemoveReservedModalityEnabled && (
                <FormattedMessage
                  id="trips.actions.changeConfirmationModalities.removeReserved"
                  defaultMessage="Remove reserved modality"
                />
              )}
              {isAddReservedModalityEnabled && (
                <FormattedMessage
                  id="trips.actions.changeConfirmationModalities.addReserved"
                  defaultMessage="Add reserved modality"
                />
              )}
              {!isChangeModalityEnabled && (
                <FormattedMessage
                  id="trips.actions.changeConfirmationModalities.label"
                  defaultMessage="Change modality"
                />
              )}
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isOpenCancelTripModal && (
        <CancelTripsModal trips={[trip]} closeModal={(): void => setOpenCancelTripModal(false)} />
      )}
      {isOpenToggleTripModal && <ToggleTripModal trip={trip} closeModal={(): void => setOpenToggleTripModal(false)} />}
      {isOpenChangeVehicleModal && (
        <ChangeTripsVehicleModal trips={[trip]} closeModal={(): void => setOpenChangeVehicleModal(false)} />
      )}
      {isOpenChangeDriverModal && (
        <ChangeDriverModal trip={trip} closeModal={(): void => setOpenChangeDriverModal(false)} />
      )}
      {isOpenChangeCrewUsersModal && (
        <ChangeCrewUsersModal trip={trip} closeModal={(): void => setOpenChangeCrewUsersModal(false)} />
      )}
      {isOpenIncidenceModal && (
        <CreateIncidenceModal
          trip={trip}
          // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
          closeModal={(): void => setOpenIncidenceModal(null)}
          onCompleted={({ subcampaignId }): TSFixMe => {
            setSuccessSnackbar({
              // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
              navigateTo: `/trips/${trip.id}/incidence-coupons?subcampaignId=${subcampaignId}`
            })
          }}
        />
      )}
      {isOpenAddReservedConfirmationModalityModal && (
        <AddReservedConfirmationModalityModal
          trips={[trip]}
          closeModal={(): void => setOpenAddReservedConfirmationModalityModal(false)}
        />
      )}
      {isOpenRemoveReservedConfirmationModalityModal && (
        <RemoveReservedConfirmationModalityModal
          trips={[trip]}
          closeModal={(): void => setOpenRemoveReservedConfirmationModalityModal(false)}
        />
      )}
      {successSnackbar && (
        <Snackbar.Container>
          <Snackbar.Content>
            <FormattedMessage id="createIncidence.success.message" defaultMessage="Coupons created successfully" />
          </Snackbar.Content>
          <Snackbar.Action
            onClick={(): TSFixMe => {
              setSuccessSnackbar(null)
              // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
              navigate(successSnackbar.navigateTo)
            }}
          >
            <FormattedMessage id="createIncidence.success.action" defaultMessage="See details" />
          </Snackbar.Action>
        </Snackbar.Container>
      )}
    </>
  )
}
